import { stripRichMarkup, type BoardNode, type NodeType } from '@kanvas/board-core';
import { useMemo, useState } from 'react';
import { setHidden, zoomToNode, type ActionContext } from '../actions/actions';
import { useNodes } from '../collab/hooks';
import { useEditor } from '../state/store';
import { Icon, type IconName } from './icons';

const TYPE_ICON: Record<NodeType, IconName> = {
  shape: 'square',
  sticky: 'sticky',
  text: 'text',
  draw: 'pen',
  connector: 'arrow',
  image: 'image',
  frame: 'frame',
  group: 'group',
  embed: 'link',
  comment: 'comment',
  notebook: 'notebook',
};

const TYPE_LABEL: Record<NodeType, string> = {
  shape: 'Фигура',
  sticky: 'Стикер',
  text: 'Текст',
  draw: 'Рисунок',
  connector: 'Соединитель',
  image: 'Изображение',
  frame: 'Фрейм',
  group: 'Группа',
  embed: 'Встраивание',
  comment: 'Комментарий',
  notebook: 'Тетрадь',
};

function describeNode(node: BoardNode): string {
  if (node.type === 'frame') return node.name || 'Фрейм';
  if (node.type === 'notebook') return node.title || 'Тетрадь';
  const text = 'text' in node ? stripRichMarkup(node.text) : undefined;
  if (typeof text === 'string' && text.trim() !== '') return text.trim().replace(/\s+/g, ' ').slice(0, 48);
  return TYPE_LABEL[node.type];
}

function matches(node: BoardNode, query: string): boolean {
  if (describeNode(node).toLowerCase().includes(query)) return true;
  if (node.tags?.some((t) => t.toLowerCase().includes(query))) return true;
  return TYPE_LABEL[node.type].toLowerCase().includes(query);
}

/**
 * Combined layers list and board search.
 *
 * One list serves both jobs: with no query it is every object in z-order
 * (top first, so it reads the same direction as "front" in the arrange menu);
 * typing filters that same list by text, tag or type. There is no separate
 * index to keep in sync — it is a live view over `doc.all()`.
 */
export function LayersPanel({ ctx }: { ctx: ActionContext }) {
  const nodes = useNodes(ctx.doc);
  const [query, setQuery] = useState('');
  const canEdit = ctx.doc.canEdit();

  const rows = useMemo(() => {
    const list = nodes.filter((n) => n.type !== 'group');
    const q = query.trim().toLowerCase();
    const filtered = q === '' ? list : list.filter((n) => matches(n, q));
    return filtered.slice().reverse();
  }, [nodes, query]);

  const open = (node: BoardNode) => {
    useEditor.getState().select([node.id]);
    zoomToNode(ctx, node.id, 1.5);
  };

  return (
    <div className="popover-stack layers-panel">
      <div className="layers-search">
        <Icon name="search" size={14} />
        <input
          type="text"
          placeholder="Поиск по тексту, тегу или типу…"
          value={query}
          autoFocus
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </div>
      <div className="layers-list">
        {rows.length === 0 && <p className="popover-hint">Ничего не найдено.</p>}
        {rows.map((node) => (
          <div key={node.id} className={`layer-row ${node.hidden ? 'is-hidden' : ''}`}>
            <button type="button" className="layer-row-main" onClick={() => open(node)}>
              <Icon name={TYPE_ICON[node.type]} size={14} />
              <span className="layer-row-label">{describeNode(node)}</span>
              {node.tags && node.tags.length > 0 && (
                <span className="layer-row-tags">#{node.tags.join(' #')}</span>
              )}
            </button>
            {canEdit && (
              <button
                type="button"
                className="layer-row-btn"
                aria-label={node.hidden ? 'Показать' : 'Скрыть'}
                data-tip={node.hidden ? 'Показать' : 'Скрыть'}
                onClick={() => setHidden(ctx.doc, [node.id], !node.hidden)}
              >
                <Icon name={node.hidden ? 'eyeOff' : 'eye'} size={14} />
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                className="layer-row-btn"
                aria-label={node.locked ? 'Разблокировать' : 'Заблокировать'}
                data-tip={node.locked ? 'Разблокировать' : 'Заблокировать'}
                onClick={() => ctx.doc.update(node.id, { locked: !node.locked })}
              >
                <Icon name={node.locked ? 'lock' : 'unlock'} size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
